import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import { RequisitionItem, useGetBomItemsByProjectByProjectIdQuery } from '../../../redux/vmsApi';
import { formatCurrency } from '../../../utils/currencyUtil';

interface RequisitionBomBudgetPanelProps {
  projectId?: string;
  items: RequisitionItem[];
}

const getThresholdStyle = (current: number, initial: number): { bgcolor: string; color: string; label: string } => {
  if (!initial || initial === 0) return { bgcolor: 'transparent', color: 'inherit', label: '' };
  const pct = (current / initial) * 100;
  if (pct < 20) return { bgcolor: '#ffebee', color: '#c62828', label: `${pct.toFixed(0)}%` };
  if (pct < 50) return { bgcolor: '#fff3e0', color: '#e65100', label: `${pct.toFixed(0)}%` };
  if (pct < 80) return { bgcolor: '#fffde7', color: '#f57f17', label: `${pct.toFixed(0)}%` };
  return { bgcolor: '#e8f5e9', color: '#2e7d32', label: `${pct.toFixed(0)}%` };
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeExact = (value: unknown) => String(value ?? '').trim();

const RequisitionBomBudgetPanel: React.FC<RequisitionBomBudgetPanelProps> = ({ projectId, items }) => {
  const { data: bomItems = [], isLoading, isError } = useGetBomItemsByProjectByProjectIdQuery(
    { projectId: projectId ?? '', limit: 3000, skip: 0 },
    { skip: !projectId }
  );

  const bomByCodeAndDescription = React.useMemo(() => {
    const map = new Map<string, any>();
    bomItems.forEach((bomItem: any) => {
      const key = `${normalizeExact(bomItem.itemCode)}|${normalizeExact(bomItem.itemDescription)}`;
      if (!map.has(key)) {
        map.set(key, bomItem);
      }
    });
    return map;
  }, [bomItems]);

  const matchedRows = React.useMemo(() => {
    return (items || [])
      .map((reqItem) => {
        const code = normalizeExact(reqItem.itemCode);
        const description = normalizeExact(reqItem.description);
        const key = `${code}|${description}`;
        const matchedBom = bomByCodeAndDescription.get(key);
        if (!matchedBom) return null;

        const quantity = toNumber(reqItem.quantity?.quantityValue);
        const requestedRate = toNumber(reqItem.rate);
        const requestedTotal = quantity * requestedRate;

        return {
          id: normalizeExact(matchedBom.id),
          itemCode: normalizeExact(matchedBom.itemCode || code),
          itemDescription: normalizeExact(matchedBom.itemDescription || description),
          itemUnit: normalizeExact(matchedBom.itemUnit || reqItem.quantity?.quantityUnit),
          itemBudgetedCost: toNumber(matchedBom.itemBudgetedCost),
          initialQuantity: toNumber(matchedBom.initialQuantity),
          itemQuantity: toNumber(matchedBom.itemQuantity),
          itemInitialValue: toNumber(matchedBom.itemInitialValue),
          itemCurrentValue: toNumber(matchedBom.itemCurrentValue),
          requestedTotal,
          isVAT: !!matchedBom.isVAT,
        };
      })
      .filter(Boolean) as Array<{
        id: string;
        itemCode: string;
        itemDescription: string;
        itemUnit: string;
        itemBudgetedCost: number;
        initialQuantity: number;
        itemQuantity: number;
        itemInitialValue: number;
        itemCurrentValue: number;
        requestedTotal: number;
        isVAT: boolean;
      }>;
  }, [items, bomByCodeAndDescription]);

  if (!projectId) return null;
  if (isLoading) {
    return (
      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }
  if (isError || matchedRows.length === 0) return null;

  const totalBom = matchedRows.reduce((sum, row) => sum + row.itemCurrentValue, 0);
  const totalRequested = matchedRows.reduce((sum, row) => sum + row.requestedTotal, 0);
  const totalVariance = totalRequested - totalBom;

  return (
    <Card
      sx={{
        mt: 3,
        borderRadius: 3,
        border: '1px solid #c8e6c9',
        background: 'linear-gradient(180deg, #f1f8e9 0%, #ffffff 70%)',
        boxShadow: '0 8px 20px rgba(46, 125, 50, 0.18)',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1b5e20' }}>
            Budget Preview
          </Typography>
         
        </Box>
        <Typography variant="body2" sx={{ color: '#33691e', mb: 2 }}>
          Showing  BOM items for this project.
        </Typography>

        <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #dcedc8' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#211680' }}>
              <TableRow>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Item Code</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Rate</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">VAT</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Initial Quantity</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Quantity</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Budget</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Remaining</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matchedRows.map((row) => (
                <TableRow key={`${row.id}-${row.itemCode}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                  <TableCell>{row.itemCode}</TableCell>
                  <TableCell>{row.itemDescription}</TableCell>
                  <TableCell>{row.itemUnit}</TableCell>
                  <TableCell align="right">{formatCurrency(row.itemBudgetedCost)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={row.isVAT ? 'VAT' : 'No VAT'}
                      color={row.isVAT ? 'warning' : 'default'}
                      variant={row.isVAT ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">{row.initialQuantity}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: getThresholdStyle(row.itemQuantity, row.initialQuantity).bgcolor }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <span>{row.itemQuantity}</span>
                      {row.initialQuantity > 0 && (
                        <span style={{ fontSize: '0.7rem', color: getThresholdStyle(row.itemQuantity, row.initialQuantity).color, fontWeight: 700 }}>
                          {getThresholdStyle(row.itemQuantity, row.initialQuantity).label} remaining
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(row.itemInitialValue)}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: getThresholdStyle(row.itemCurrentValue, row.itemInitialValue).bgcolor }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <span>{formatCurrency(row.itemCurrentValue)}</span>
                      {row.itemInitialValue > 0 && (
                        <span style={{ fontSize: '0.7rem', color: getThresholdStyle(row.itemCurrentValue, row.itemInitialValue).color, fontWeight: 700 }}>
                          {getThresholdStyle(row.itemCurrentValue, row.itemInitialValue).label} remaining
                        </span>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        
      </CardContent>
    </Card>
  );
};

export default RequisitionBomBudgetPanel;
